import sessionApi from './apiClient';
import { ApiResponse, LoginRequest, LoginResponse } from '../types/auth';
import { AuthProfile, User } from '../types/domain';
import { ErrorHandler } from '../utils/errorHandler';

export const isLoginResponse = (value: unknown): value is LoginResponse => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const response = value as Record<string, unknown>;
  const hasExpectedProperty = (
    typeof response.success === 'boolean'
    || typeof response.error === 'string'
    || typeof response.mfa_required === 'boolean'
    || typeof response.recaptcha_required === 'boolean'
  );
  if (!hasExpectedProperty) return false;
  if (response.success !== undefined && typeof response.success !== 'boolean') return false;
  if (response.error !== undefined && typeof response.error !== 'string') return false;
  if (response.mfa_required !== undefined && typeof response.mfa_required !== 'boolean') return false;
  if (response.recaptcha_required !== undefined && typeof response.recaptcha_required !== 'boolean') return false;
  return true;
};

const invalidLoginResponseError = () => ErrorHandler.classifyError({
  response: {
    status: 502,
    data: { detail: 'The authentication service returned an invalid response.' },
  },
});

export const authService = {
  login: async (credentials: LoginRequest): Promise<LoginResponse> => {
    try {
      const formData = new FormData();
      formData.append('username', credentials.username);
      formData.append('password', credentials.password);
      if (credentials.recaptcha_token) formData.append('recaptcha_token', credentials.recaptcha_token);

      const response = await sessionApi.post<LoginResponse>('/auth/login/', formData);
      if (!isLoginResponse(response.data)) throw invalidLoginResponseError();
      return response.data;
    } catch (error) {
      const responseData = (error as any)?.response?.data || (error as any)?.originalError?.response?.data;
      if (isLoginResponse(responseData)) return responseData;
      throw ErrorHandler.classifyError(error);
    }
  },

  getMfaSetup: async (): Promise<LoginResponse> => {
    const response = await sessionApi.get<LoginResponse>('/auth/mfa/setup/');
    return response.data;
  },

  verifyMfa: async (code: string): Promise<LoginResponse> => {
    const response = await sessionApi.post<LoginResponse>('/auth/mfa/verify/', { code });
    return response.data;
  },

  logout: async (): Promise<void> => {
    try {
      const csrfResponse = await sessionApi.get<{ csrf_token: string }>('/auth/csrf/');
      await sessionApi.post('/auth/logout/', undefined, {
        headers: { 'X-CSRFToken': csrfResponse.data.csrf_token },
      });
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  getProfile: async (): Promise<AuthProfile & ApiResponse<User>> => {
    try {
      const response = await sessionApi.get<AuthProfile & ApiResponse<User>>('/auth/profile/');
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  changePassword: async (oldPassword: string, newPassword: string): Promise<ApiResponse<null>> => {
    try {
      const csrfResponse = await sessionApi.get<{ csrf_token: string }>('/auth/csrf/');
      const response = await sessionApi.post<ApiResponse<null>>(
        '/auth/change-password/',
        { old_password: oldPassword, new_password: newPassword },
        {
          headers: { 'X-CSRFToken': csrfResponse.data.csrf_token },
        }
      );
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  requestPasswordReset: async (identifier: string): Promise<ApiResponse<null>> => {
    try {
      const csrfResponse = await sessionApi.get<{ csrf_token: string }>('/auth/csrf/');
      const response = await sessionApi.post<ApiResponse<null>>(
        '/auth/forgot-password/',
        { identifier },
        {
          headers: { 'X-CSRFToken': csrfResponse.data.csrf_token },
        }
      );
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },

  resetPassword: async (
    uid: string,
    token: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<ApiResponse<null>> => {
    try {
      const csrfResponse = await sessionApi.get<{ csrf_token: string }>('/auth/csrf/');
      const response = await sessionApi.post<ApiResponse<null>>(
        '/auth/reset-password/',
        {
          uid,
          token,
          new_password: newPassword,
          confirm_password: confirmPassword,
        },
        {
          headers: { 'X-CSRFToken': csrfResponse.data.csrf_token },
        }
      );
      return response.data;
    } catch (error) {
      throw ErrorHandler.classifyError(error);
    }
  },
};
